import { useState, useEffect, useRef } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { LayoutDashboard, CalendarDays, Users, Inbox, Settings as SettingsIcon, LogOut, Clock, Sparkles, Euro, MessageCircle, Star, Image, Menu, X, Megaphone, CheckSquare, Sun, RefreshCw, BarChart3 } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { api } from "../../lib/api";
import { InstallPWA } from "../../components/InstallPWA";
import { playChatChime, primeChatSound } from "../../lib/chatSound";

const LINKS = [
  { to: "/admin", label: "Dashboard", short: "Home", icon: LayoutDashboard, end: true, primary: true },
  { to: "/admin/agenda", label: "Agenda", short: "Agenda", icon: CalendarDays, primary: true },
  { to: "/admin/todos", label: "To-Do's", short: "To-Do", icon: CheckSquare },
  { to: "/admin/behandelingen", label: "Behandelingen", short: "Behand.", icon: Sparkles },
  { to: "/admin/reviews", label: "Reviews", short: "Reviews", icon: Star },
  { to: "/admin/galerij", label: "Galerij", short: "Galerij", icon: Image },
  { to: "/admin/openingstijden", label: "Openingstijden", short: "Tijden", icon: Clock },
  { to: "/admin/klanten", label: "Klanten", short: "Klanten", icon: Users, primary: true },
  { to: "/admin/aanvragen", label: "Aanvragen", short: "Aanvr.", icon: Inbox, primary: true },
  { to: "/admin/campagnes", label: "Campagnes", short: "Camp.", icon: Megaphone },
  { to: "/admin/chat", label: "Chat", short: "Chat", icon: MessageCircle },
  { to: "/admin/financieel", label: "Financieel", short: "Financ.", icon: Euro },
  { to: "/admin/rapport", label: "Rapport", short: "Rapport", icon: BarChart3 },
  { to: "/admin/instellingen", label: "Instellingen", short: "Opties", icon: SettingsIcon },
];

const PRIMARY = LINKS.filter((l) => l.primary);

export default function AdminLayout() {
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [newRequests, setNewRequests] = useState(0);
  const [chatUnread, setChatUnread] = useState(0);
  const prevChat = useRef(null);
  const doLogout = () => { logout(); navigate("/admin/login"); };

  useEffect(() => {
    let id;
    let last = Date.now();
    const bump = () => { last = Date.now(); };
    const evs = ["mousedown", "keydown", "touchstart", "pointerdown", "input"];
    evs.forEach((e) => window.addEventListener(e, bump, { passive: true }));
    const busy = () => {
      const el = document.activeElement;
      if (el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.tagName === "SELECT" || el.isContentEditable)) return true;
      if (document.querySelector('[role="dialog"]')) return true;
      return Date.now() - last < 30000;
    };
    api.get("/admin/settings").then((r) => {
      const min = Number(r.data?.auto_refresh_min);
      if (min > 0) id = setInterval(() => { if (!busy()) window.location.reload(); }, min * 60000);
    }).catch(() => {});
    return () => { if (id) clearInterval(id); evs.forEach((e) => window.removeEventListener(e, bump)); };
  }, []);

  useEffect(() => {
    let active = true;
    const load = () => api.get("/admin/new-requests-count")
      .then((r) => { if (active) setNewRequests(r.data.total || 0); })
      .catch(() => {});
    load();
    const id = setInterval(load, 60000);
    return () => { active = false; clearInterval(id); };
  }, [location.pathname]);

  // Prime audio on first user gesture so the chime can play later.
  useEffect(() => {
    const prime = () => { primeChatSound(); window.removeEventListener("pointerdown", prime); };
    window.addEventListener("pointerdown", prime);
    return () => window.removeEventListener("pointerdown", prime);
  }, []);

  // Poll unread chat count; play a chime when it increases.
  useEffect(() => {
    let active = true;
    const load = () => api.get("/admin/chat/sessions").then((r) => {
      if (!active) return;
      const total = (r.data || []).reduce((a, s) => a + (s.unread_admin || 0), 0);
      if (prevChat.current !== null && total > prevChat.current) playChatChime();
      prevChat.current = total;
      setChatUnread(total);
    }).catch(() => {});
    load();
    const id = setInterval(load, 15000);
    return () => { active = false; clearInterval(id); };
  }, []);

  const badgeFor = (to) => (to === "/admin/aanvragen" ? newRequests : to === "/admin/chat" ? chatUnread : 0);

  return (
    <div className="min-h-screen bg-cream-admin flex">
      {/* desktop sidebar */}
      <aside className="w-64 shrink-0 border-r border-[#E8E2D9] bg-white hidden md:flex flex-col fixed inset-y-0">
        <a href="/" data-testid="admin-logo-home" title="Naar de website"
          className="h-20 flex items-center gap-2.5 px-6 font-display text-lg text-ink border-b border-[#E8E2D9] hover:text-brand transition-colors">
          <img src="/logo-round.png" alt="Nelleke Holt" className="w-8 h-8" /> Nelleke Holt
        </a>
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {LINKS.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.end} data-testid={`nav-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm transition-colors duration-200 ${
                  isActive ? "bg-brand-light text-brand" : "text-ink-muted hover:bg-cream-subtle"
                }`}>
              <l.icon className="w-4 h-4" strokeWidth={1.5} /> <span>{l.label}</span>
              {badgeFor(l.to) > 0 && (
                <span data-testid={`nav-${l.label.toLowerCase()}-badge`}
                  className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-brand text-white text-[11px] font-medium">
                  {badgeFor(l.to)}
                </span>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-[#E8E2D9]">
          <button onClick={() => window.location.reload()} data-testid="admin-refresh"
            className="flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm text-ink-muted hover:bg-cream-subtle w-full transition-colors mb-1">
            <RefreshCw className="w-4 h-4" strokeWidth={1.5} /> Ververs scherm
          </button>
          <Link to="/planner" data-testid="admin-to-planner"
            className="flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm text-ink-muted hover:bg-cream-subtle w-full transition-colors mb-1">
            <Sun className="w-4 h-4" strokeWidth={1.5} /> NH Planner
          </Link>
          <InstallPWA variant="sidebar" />
          <p className="px-4 text-xs text-ink-muted mb-2 mt-1 truncate">{user?.email}</p>
          <button onClick={doLogout} data-testid="logout-btn"
            className="flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm text-ink-muted hover:bg-cream-subtle w-full transition-colors">
            <LogOut className="w-4 h-4" strokeWidth={1.5} /> Uitloggen
          </button>
        </div>
      </aside>

      {/* mobile top bar */}
      <div className="md:hidden fixed top-0 inset-x-0 z-40 bg-white border-b border-[#E8E2D9] h-16 flex items-center justify-between px-4">
        <button onClick={() => setMenuOpen(true)} data-testid="admin-menu-btn" aria-label="Menu" className="p-1 -ml-1 text-ink">
          <Menu className="w-6 h-6" />
        </button>
        <a href="/" data-testid="admin-logo-home-mobile" title="Naar de website" className="font-display text-ink flex items-center gap-2 hover:text-brand transition-colors">
          <img src="/logo-round.png" alt="Nelleke Holt" className="w-7 h-7" /> Beheer
        </a>
        <div className="flex items-center gap-4">
          <button onClick={() => window.location.reload()} data-testid="admin-refresh-mobile" aria-label="Ververs scherm" className="text-ink-muted hover:text-brand transition-colors"><RefreshCw className="w-5 h-5" /></button>
          <Link to="/planner" data-testid="admin-to-planner-mobile" aria-label="Naar NH Planner" className="text-ink-muted hover:text-brand transition-colors">
            <Sun className="w-5 h-5" />
          </Link>
          <button onClick={doLogout} aria-label="Uitloggen"><LogOut className="w-5 h-5 text-ink-muted" /></button>
        </div>
      </div>

      {/* mobile slide-in drawer */}
      {menuOpen && (
        <div className="md:hidden fixed inset-0 z-50" data-testid="admin-drawer">
          <div className="absolute inset-0 bg-ink/40 backdrop-blur-sm" onClick={() => setMenuOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-72 max-w-[85%] bg-white shadow-2xl flex flex-col animate-[slideIn_0.25s_ease]">
            <div className="h-16 flex items-center justify-between px-5 border-b border-[#E8E2D9]">
              <span className="font-display text-lg text-ink flex items-center gap-2"><img src="/logo-round.png" alt="" className="w-7 h-7" /> Nelleke Holt</span>
              <button onClick={() => setMenuOpen(false)} data-testid="admin-drawer-close" aria-label="Sluiten"><X className="w-5 h-5 text-ink-muted" /></button>
            </div>
            <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
              {LINKS.map((l) => (
                <NavLink key={l.to} to={l.to} end={l.end} onClick={() => setMenuOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-3 rounded-xl text-sm transition-colors ${
                      isActive ? "bg-brand-light text-brand" : "text-ink-muted hover:bg-cream-subtle"
                    }`}>
                  <l.icon className="w-5 h-5" strokeWidth={1.5} /> <span>{l.label}</span>
                  {badgeFor(l.to) > 0 && (
                    <span className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-brand text-white text-[11px] font-medium">
                      {badgeFor(l.to)}
                    </span>
                  )}
                </NavLink>
              ))}
            </nav>
            <div className="p-4 border-t border-[#E8E2D9]">
              <Link to="/planner" onClick={() => setMenuOpen(false)} data-testid="admin-to-planner-drawer"
                className="flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm text-ink-muted hover:bg-cream-subtle w-full transition-colors mb-1">
                <Sun className="w-4 h-4" strokeWidth={1.5} /> NH Planner
              </Link>
              <InstallPWA variant="sidebar" />
              <p className="px-4 text-xs text-ink-muted mb-2 mt-1 truncate">{user?.email}</p>
              <button onClick={doLogout} className="flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm text-ink-muted hover:bg-cream-subtle w-full transition-colors">
                <LogOut className="w-4 h-4" strokeWidth={1.5} /> Uitloggen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* mobile bottom nav: primary items + Meer */}
      <div className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-[#E8E2D9] flex justify-around py-2">
        {PRIMARY.map((l) => (
          <NavLink key={l.to} to={l.to} end={l.end} data-testid={`bottom-nav-${l.label.toLowerCase()}`}
            className={({ isActive }) => `relative flex flex-col items-center gap-0.5 text-[10px] px-2 ${isActive ? "text-brand" : "text-ink-muted"}`}>
            <span className="relative">
              <l.icon className="w-5 h-5" strokeWidth={1.5} />
              {badgeFor(l.to) > 0 && (
                <span data-testid="bottom-nav-aanvragen-badge"
                  className="absolute -top-1.5 -right-2 inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-brand text-white text-[9px] font-medium">
                  {badgeFor(l.to)}
                </span>
              )}
            </span>
            {l.short}
          </NavLink>
        ))}
        <button onClick={() => setMenuOpen(true)} data-testid="bottom-nav-meer"
          className="flex flex-col items-center gap-0.5 text-[10px] px-2 text-ink-muted">
          <Menu className="w-5 h-5" strokeWidth={1.5} /> Meer
        </button>
      </div>

      <main className="flex-1 min-w-0 overflow-x-hidden md:ml-64 pt-16 md:pt-0 pb-24 md:pb-0">
        <div className="max-w-6xl mx-auto p-4 sm:p-5 md:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
